import { useCallback, useSyncExternalStore } from 'react';
import { useNavigate, type NavigateOptions, type To } from 'react-router';
import { create } from 'zustand';
import type { Design } from '@noorview/core';

export type SheetState =
  | { name: 'schedule'; screenId?: string }
  | { name: 'pair'; screenId?: string }
  | { name: 'addScreen' }
  | { name: 'csv' }
  | { name: 'jumuah' }
  | { name: 'eid' }
  | { name: 'search' }
  | { name: 'upgrade'; reason?: 'screens' | 'users' }
  | { name: 'logo'; first?: boolean }
  | { name: 'message' }
  | { name: 'khutbah' }
  | { name: 'announce'; screenId: string; widgetId?: string }
  | { name: 'appeal' }
  /** Editing a live or scheduled message or appeal; `back` reopens the sheet it was opened from. */
  | { name: 'editBroadcast'; id: string; back?: SheetState }
  | { name: 'analytics' }
  | { name: 'fullscreen'; screenId: string; design?: Design; label?: string };

let closeTimer: ReturnType<typeof setTimeout> | null = null;
/** The open sheet. Closing animates it away first (`closing`), then removes it. */
export const useSheet = create<{ sheet: SheetState | null; closing: boolean; open: (s: SheetState) => void; close: () => void }>((set, get) => ({
  sheet: null,
  closing: false,
  open: (sheet) => { if (closeTimer) clearTimeout(closeTimer); closeTimer = null; set({ sheet, closing: false }); },
  close: () => {
    if (!get().sheet || get().closing) return;
    set({ closing: true });
    closeTimer = setTimeout(() => { closeTimer = null; set({ sheet: null, closing: false }); }, 200);
  },
}));
/** Sheets that change something; while NoorView is locked they're refused (see app/lock). */
const EDITING = new Set<SheetState['name']>(['schedule', 'pair', 'addScreen', 'csv', 'jumuah', 'eid', 'logo', 'message', 'appeal', 'announce', 'editBroadcast', 'khutbah']);
let sheetGuard: ((open: () => void) => void) | null = null;
export const setSheetGuard = (g: typeof sheetGuard) => { sheetGuard = g; };
export const openSheet = (s: SheetState) => {
  const open = () => useSheet.getState().open(s);
  if (EDITING.has(s.name) && sheetGuard) sheetGuard(open); else open();
};

/** Which screen the dashboard's large preview shows. */
export const useFocusScreen = create<{ id: string | null; set: (id: string) => void }>((set) => ({ id: null, set: (id) => set({ id }) }));

/** True while a CSS media query matches (re-renders when it changes). */
export function useMedia(query: string): boolean {
  return useSyncExternalStore(
    (cb) => { const m = window.matchMedia(query); m.addEventListener('change', cb); return () => m.removeEventListener('change', cb); },
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/** Which Studio panels are open when the window is too small to show them all. */
export const useStudioPanels = create<{ library: boolean; inspector: boolean; set: (p: Partial<{ library: boolean; inspector: boolean }>) => void }>((set) => ({
  library: false, inspector: false, set: (p) => set(p),
}));

/**
 * Moves to another page with a smooth transition (the sidebar and page glide to their new layout instead of jumping).
 * Browsers without view transitions simply change page.
 */
export function useNav() {
  const n = useNavigate();
  return useCallback((to: To, opts?: NavigateOptions) => { void n(to, { viewTransition: true, ...opts }); }, [n]);
}
